/**
 * Sidebar Component
 * Navigation sidebar with collapsible menus
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VerifyDocumentDialog from '../qr/VerifyDocumentDialog';
import { logQrScan } from '../../services/eventLogs';
import type { VerificationResult } from '../../utils/documentVerification';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  useTheme as useMuiTheme,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as StructuresIcon,
  LocalShipping as TransporterIcon,
  People as DriversIcon,
  AirlineSeatReclineNormal as DriverSpaceIcon,
  ExpandLess,
  ExpandMore,
  Store as AgencyIcon,
  PersonAdd as PersonAddIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Public as CountriesIcon,
  DirectionsCar as BrandsIcon,
  Category as VehicleTypesIcon,
  Security as InsurersIcon,
  Inventory as CategoriesIcon,
  QrCode as HSCodesIcon,
  AltRoute as ShippersIcon,
  Archive as PackagingTypesIcon,
  ViewInAr as ContainerTypesIcon,
  AttachMoney as CurrenciesIcon,
  Tune as ParametersIcon,
  CreditCard as PaymentMethodsIcon,
  Timeline as CorridorsIcon,
  Route as ItinerariesIcon,
  LinearScale as SegmentsIcon,
  LocationOn as CheckpointsIcon,
  DirectionsBus as VehicleFleetIcon,
  AddCircle as AddVehicleIcon,
  List as ListIcon,
  Description as FreightDeclarationIcon,
  NoteAdd as FreightFormIcon,
  ListAlt as FreightListIcon,
  Receipt as WaybillIcon,
  History as AmendmentHistoryIcon,
  AccountBalance as FinanceIcon,
  ReceiptLong as InvoicesIcon,
  Payments as RemittancesIcon,
  AddCard as RemittanceRecordIcon,
  Insights as RemittanceTrackingIcon,
  GpsFixed as CheckpointManagementIcon,
  TransferWithinAStation as CheckpointPassageIcon,
  EventNote as CheckpointEventsIcon,
  SyncAlt as SyncHistoryIcon,
  AccountBalanceWallet as TaeIcon,
  FormatListBulleted as TaeListIcon,
  AddCircleOutline as TaeRenewalIcon,
  DirectionsCar as TaeVehicleControlIcon,
  QrCodeScanner as QrCodeScannerIcon,
  History as EventLogsIcon,
  AdminPanelSettings as AdminIcon,
  CompareArrows as BgftExchangesIcon,
} from '@mui/icons-material';

import { useTheme } from '../../store/ThemeContext';
import { useAuth } from '../../store/AuthContext';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  width: number;
  isMobile: boolean;
}

interface MenuItem {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
  path?: string;
  action?: string;
  children?: MenuItem[];
  requiredProfiles?: string[];
  requiresStructure?: boolean;
  requiresStructureAdmin?: boolean;
  // Restrict the menu to one structure type, or to any type in the list.
  requiresStructureType?: string | string[];
  // Hide menu when the user's structure has individual=true (Particulier).
  // A particulier carries no real structure / agencies to manage.
  hideIfIndividualStructure?: boolean;
  // Hide the menu for these structure types, whatever the user's profiles.
  // Carriers inherit ADMIN on their own structure, which would otherwise open
  // platform-wide menus (Émission de lettre, Administration, ...) to them.
  hiddenForStructureTypes?: string[];
  // Always show the menu for these structure types, whatever the user's
  // profiles. Used for the screens a structure type owns by nature — a carrier
  // consults its own waybills and TAEs without needing a dedicated role.
  allowedForStructureTypes?: string[];
  // Restrict the menu to these user types (User.type, e.g. DRIVER).
  requiresUserType?: string[];
  // Hide the menu for these user types, whatever their structure or profiles.
  // A driver sits inside a CARRIER structure, so the carrier-owner menus would
  // otherwise open the whole fleet to them.
  hiddenForUserTypes?: string[];
}

// Menu configuration
const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    labelKey: 'dashboard.menu.dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
  },
  {
    id: 'verify-document',
    labelKey: 'common.documentVerification.title',
    icon: <QrCodeScannerIcon />,
    action: 'openVerifyDocument',
  },
  {
    id: 'transporters',
    labelKey: 'dashboard.menu.transporters',
    icon: <TransporterIcon />,
    requiredProfiles: ['MANAGE_CARRIERS', 'CREATE_STRUCTURES', 'VEHICLE_AGENT', 'MANAGE_VEHICLE_FLEET', 'VEHICLE_MANAGER'],
    // A carrier always reaches this menu (at least for its own waybills);
    // the children stay individually gated.
    allowedForStructureTypes: ['CARRIER'],
    // Drivers belong to a carrier structure but get their own "Conducteur"
    // menu, scoped to the lots they drive.
    hiddenForUserTypes: ['DRIVER'],
    children: [
      {
        id: 'transporters-registry',
        labelKey: 'dashboard.menu.transportersRegistry',
        icon: <PersonAddIcon />,
        path: '/dashboard/transporters',
        requiredProfiles: ['MANAGE_CARRIERS', 'CREATE_STRUCTURES'],
      },
      {
        id: 'transporters-add',
        labelKey: 'dashboard.menu.addTransporter',
        icon: <PersonAddIcon />,
        path: '/dashboard/transporters/add',
        requiredProfiles: ['CREATE_STRUCTURES', 'MANAGE_CARRIERS'],
      },
      {
        id: 'vehicle-fleet',
        labelKey: 'dashboard.menu.vehicleFleet',
        icon: <VehicleFleetIcon />,
        path: '/dashboard/transporters/vehicles',
        requiredProfiles: ['VEHICLE_AGENT', 'MANAGE_VEHICLE_FLEET', 'VEHICLE_MANAGER'],
      },
      {
        id: 'vehicle-add',
        labelKey: 'dashboard.menu.addVehicle',
        icon: <AddVehicleIcon />,
        path: '/dashboard/transporters/vehicles/add',
        requiredProfiles: ['VEHICLE_AGENT', 'MANAGE_VEHICLE_FLEET', 'VEHICLE_MANAGER'],
      },
      {
        id: 'carrier-waybills',
        labelKey: 'dashboard.menu.carrierWaybills',
        icon: <WaybillIcon />,
        path: '/dashboard/transporters/waybills',
        // No profile gate: a carrier always consults the waybills issued for
        // its own fleet. Being a CARRIER structure is the only condition.
        requiresStructureType: 'CARRIER',
      },
    ],
  },
  {
    id: 'drivers-registry',
    labelKey: 'dashboard.menu.driversRegistry',
    icon: <DriversIcon />,
    path: '/dashboard/drivers/registry',
    requiredProfiles: ['MANAGE_DRIVERS'],
    // The platform manager sees every driver just like the main OGF
    // (INSTRUCTIONS.md), so MANAGER is allowed alongside OGF.
    requiresStructureType: ['OGF', 'MANAGER'],
  },
  {
    id: 'driver-space',
    labelKey: 'dashboard.menu.driverSpace',
    icon: <DriverSpaceIcon />,
    // Reserved to drivers; no profile needed beyond being the assigned driver.
    requiresUserType: ['DRIVER'],
    children: [
      {
        id: 'driver-waybills',
        labelKey: 'dashboard.menu.driverWaybills',
        icon: <WaybillIcon />,
        path: '/dashboard/driver/waybills',
        requiresUserType: ['DRIVER'],
      },
      {
        id: 'driver-tae',
        labelKey: 'dashboard.menu.driverTae',
        icon: <TaeListIcon />,
        // Same screen as "TAE > Liste des TAE": the endpoint already scopes a
        // carrier-structure member to the TAEs of their own structure.
        path: '/dashboard/tae/list',
        requiresUserType: ['DRIVER'],
      },
    ],
  },
  {
    id: 'freight-declaration',
    labelKey: 'dashboard.menu.freightDeclaration',
    icon: <FreightDeclarationIcon />,
    requiredProfiles: ['FREIGHT_MANAGER', 'FREIGHT_AGENT', 'MAKE_FREIGHT_DECLARATION', 'MAKE_FREIGHT_CALIBRATION', 'AGENCY_HEAD', 'AMEND_LV', 'ADMIN', 'ACCOUNTING_OFFICER', 'GLOBAL_ACCOUNTING_OFFICER', 'FRET_STATISTIC', 'GLOBAL_FRET_STATISTIC'],
    // Carriers never emit waybills; they only consult the ones issued for them
    // (Transporteur > Lettres de voiture).
    hiddenForStructureTypes: ['CARRIER'],
    children: [
      {
        id: 'freight-form',
        labelKey: 'dashboard.menu.freightSimplifiedForm',
        icon: <FreightFormIcon />,
        path: '/dashboard/freight/form',
        requiredProfiles: ['FREIGHT_MANAGER', 'FREIGHT_AGENT', 'MAKE_FREIGHT_DECLARATION', 'MAKE_FREIGHT_CALIBRATION'],
      },
      {
        id: 'freight-list',
        labelKey: 'dashboard.menu.freightList',
        icon: <FreightListIcon />,
        path: '/dashboard/freight/list',
        // FRET_STATISTIC / GLOBAL_FRET_STATISTIC: read-only (filters and export), their agency / every agency.
        requiredProfiles: ['FREIGHT_MANAGER', 'FREIGHT_AGENT', 'MAKE_FREIGHT_DECLARATION', 'MAKE_FREIGHT_CALIBRATION', 'FRET_STATISTIC', 'GLOBAL_FRET_STATISTIC'],
      },
      {
        id: 'waybills',
        labelKey: 'dashboard.menu.waybills',
        icon: <WaybillIcon />,
        path: '/dashboard/freight/waybills',
        // ACCOUNTING_OFFICER / GLOBAL_ACCOUNTING_OFFICER: read-only, their agency / structure.
        // FRET_STATISTIC / GLOBAL_FRET_STATISTIC: read-only (filters and export), their agency / every agency.
        requiredProfiles: ['FREIGHT_MANAGER', 'FREIGHT_AGENT', 'MAKE_FREIGHT_DECLARATION', 'MAKE_FREIGHT_CALIBRATION', 'ACCOUNTING_OFFICER', 'GLOBAL_ACCOUNTING_OFFICER', 'FRET_STATISTIC', 'GLOBAL_FRET_STATISTIC'],
      },
      {
        id: 'amendment-history',
        labelKey: 'dashboard.menu.amendmentHistory',
        icon: <AmendmentHistoryIcon />,
        path: '/dashboard/freight/amendments',
        requiredProfiles: ['FREIGHT_MANAGER', 'FREIGHT_AGENT', 'MAKE_FREIGHT_DECLARATION', 'MAKE_FREIGHT_CALIBRATION', 'AGENCY_HEAD', 'AMEND_LV', 'ADMIN'],
      },
    ],
  },
  {
    id: 'finance',
    labelKey: 'dashboard.menu.finance',
    icon: <FinanceIcon />,
    // The children stay individually gated; the parent hides when none is left.
    requiredProfiles: [
      'CASHIER',
      'ACCOUNTING_OFFICER',
      'GLOBAL_ACCOUNTING_OFFICER',
      'CASH_TRACKING',
      'GLOBAL_CASH_TRACKING',
      'ADMIN',
      'AGENCY_HEAD',
    ],
    children: [
      {
        id: 'invoices',
        labelKey: 'dashboard.menu.receipts',
        icon: <InvoicesIcon />,
        path: '/dashboard/finance/invoices',
        // ACCOUNTING_OFFICER: their agency; GLOBAL_ACCOUNTING_OFFICER: every
        // agency of their structure (scoped by the API).
        requiredProfiles: ['CASHIER', 'ACCOUNTING_OFFICER', 'GLOBAL_ACCOUNTING_OFFICER'],
      },
      // Aggregator remittances: OGF structures only. CASH_TRACKING and
      // AGENCY_HEAD are scoped to their agency by the API, ADMIN and
      // GLOBAL_CASH_TRACKING see every agency of the structure.
      {
        id: 'remittances',
        labelKey: 'dashboard.menu.remittances',
        icon: <RemittancesIcon />,
        path: '/dashboard/finance/remittances',
        requiredProfiles: ['CASH_TRACKING', 'GLOBAL_CASH_TRACKING', 'ADMIN', 'AGENCY_HEAD'],
        requiresStructureType: 'OGF',
      },
      {
        id: 'remittance-record',
        labelKey: 'dashboard.menu.remittanceRecord',
        icon: <RemittanceRecordIcon />,
        path: '/dashboard/finance/remittances/new',
        requiredProfiles: ['CASH_TRACKING', 'GLOBAL_CASH_TRACKING', 'ADMIN', 'AGENCY_HEAD'],
        requiresStructureType: 'OGF',
      },
      {
        id: 'remittance-tracking',
        labelKey: 'dashboard.menu.remittanceTracking',
        icon: <RemittanceTrackingIcon />,
        path: '/dashboard/finance/remittances/tracking',
        requiredProfiles: ['CASH_TRACKING', 'GLOBAL_CASH_TRACKING', 'ADMIN', 'AGENCY_HEAD'],
        requiresStructureType: 'OGF',
      },
    ],
  },
  {
    id: 'checkpoint-management',
    labelKey: 'dashboard.menu.checkpointManagement',
    icon: <CheckpointManagementIcon />,
    // AGENCY_HEAD consults the passages of their agency's checkpoints; the
    // children stay individually gated (registering is CHECKPOINT-only).
    requiredProfiles: ['CHECKPOINT', 'ADMIN', 'AGENCY_HEAD'],
    requiresStructureType: 'OGF',
    children: [
      {
        id: 'checkpoint-passage',
        labelKey: 'dashboard.menu.checkpointPassage',
        icon: <CheckpointPassageIcon />,
        path: '/dashboard/checkpoint/passages',
        requiredProfiles: ['CHECKPOINT', 'ADMIN', 'AGENCY_HEAD'],
      },
      {
        id: 'checkpoint-events',
        labelKey: 'dashboard.menu.checkpointEvents',
        icon: <CheckpointEventsIcon />,
        path: '/dashboard/checkpoint/events',
        requiredProfiles: ['CHECKPOINT', 'ADMIN'],
      },
      {
        id: 'offline-sync-history',
        labelKey: 'dashboard.menu.offlineSyncHistory',
        icon: <SyncHistoryIcon />,
        path: '/dashboard/checkpoint/sync-history',
        requiredProfiles: ['CHECKPOINT', 'ADMIN'],
      },
    ],
  },
  {
    id: 'tae',
    labelKey: 'dashboard.menu.tae',
    icon: <TaeIcon />,
    requiredProfiles: ['ADMIN', 'FREIGHT_MANAGER', 'FREIGHT_AGENT', 'MAKE_FREIGHT_DECLARATION', 'MAKE_FREIGHT_CALIBRATION', 'CHECKPOINT', 'TAE_EMITTER', 'ACCOUNTING_OFFICER', 'GLOBAL_ACCOUNTING_OFFICER'],
    // A carrier always reaches this menu (for "Liste des TAE" only, the other
    // children are hidden for CARRIER).
    allowedForStructureTypes: ['CARRIER'],
    // Drivers reach the same list through "Conducteur > TAE".
    hiddenForUserTypes: ['DRIVER'],
    children: [
      {
        id: 'tae-list',
        labelKey: 'dashboard.menu.taeList',
        icon: <TaeListIcon />,
        path: '/dashboard/tae/list',
        // Restricted: "Liste des TAE" only for ADMIN and TAE_EMITTER (plus the
        // read-only ACCOUNTING_OFFICER / GLOBAL_ACCOUNTING_OFFICER)...
        requiredProfiles: ['ADMIN', 'TAE_EMITTER', 'ACCOUNTING_OFFICER', 'GLOBAL_ACCOUNTING_OFFICER'],
        // ...except for carriers, who always consult the TAEs of their own
        // fleet without needing a dedicated role.
        allowedForStructureTypes: ['CARRIER'],
      },
      {
        id: 'tae-renewal',
        labelKey: 'dashboard.menu.taeRenewal',
        icon: <TaeRenewalIcon />,
        path: '/dashboard/tae/renewal',
        // Restricted: "Ajout / Renouvellement" only for ADMIN and TAE_EMITTER.
        requiredProfiles: ['ADMIN', 'TAE_EMITTER'],
        // Carriers only need "Liste des TAE".
        hiddenForStructureTypes: ['CARRIER'],
      },
      {
        id: 'tae-vehicle-control',
        labelKey: 'dashboard.menu.taeVehicleControl',
        icon: <TaeVehicleControlIcon />,
        path: '/dashboard/tae/vehicle-control',
        requiredProfiles: ['ADMIN', 'FREIGHT_MANAGER', 'FREIGHT_AGENT', 'MAKE_FREIGHT_DECLARATION', 'MAKE_FREIGHT_CALIBRATION', 'CHECKPOINT', 'TAE_EMITTER'],
        // Carriers only need "Liste des TAE".
        hiddenForStructureTypes: ['CARRIER'],
      },
    ],
  },
  {
    id: 'admin',
    labelKey: 'dashboard.menu.admin',
    icon: <AdminIcon />,
    requiredProfiles: ['ADMIN'],
    // Platform-wide administration: never for carriers, even though a carrier
    // structure admin carries the ADMIN profile on its own structure.
    hiddenForStructureTypes: ['CARRIER'],
    children: [
      {
        id: 'admin-event-logs',
        labelKey: 'dashboard.menu.eventLogs',
        icon: <EventLogsIcon />,
        path: '/dashboard/admin/event-logs',
        requiredProfiles: ['ADMIN'],
      },
    ],
  },
  {
    id: 'my-structure',
    labelKey: 'dashboard.menu.myStructure',
    icon: <StructuresIcon />,
    requiresStructure: true,
    hideIfIndividualStructure: true,
    children: [
      {
        id: 'my-structure-info',
        labelKey: 'dashboard.menu.structureInfo',
        icon: <StructuresIcon />,
        path: '/dashboard/my-structure/info',
        requiredProfiles: ['UPDATE_STRUCTURE'],
        requiresStructureAdmin: true,
      },
      {
        id: 'my-structure-users',
        labelKey: 'dashboard.menu.usersList',
        icon: <DriversIcon />,
        path: '/dashboard/my-structure/users',
        // AGENCY_HEAD (Chef d'agence) may manage users of their own agency even
        // without being a structure ADMIN.
        requiredProfiles: ['LIST_USERS', 'AGENCY_HEAD'],
        requiresStructureAdmin: true,
      },
      {
        id: 'my-structure-add-user',
        labelKey: 'dashboard.menu.addUser',
        icon: <PersonAddIcon />,
        path: '/dashboard/my-structure/users/add',
        requiredProfiles: ['CREATE_USER', 'AGENCY_HEAD'],
        requiresStructureAdmin: true,
      },
    ],
  },
  {
    id: 'my-agencies',
    labelKey: 'dashboard.menu.myAgencies',
    icon: <AgencyIcon />,
    requiresStructure: true,
    requiredProfiles: ['UPDATE_STRUCTURE'],
    hideIfIndividualStructure: true,
    children: [
      {
        id: 'my-agencies-list',
        labelKey: 'dashboard.menu.agenciesList',
        icon: <AgencyIcon />,
        path: '/dashboard/my-structure/agencies',
        requiresStructure: true,
      },
      {
        id: 'my-agencies-add',
        labelKey: 'dashboard.menu.addAgency',
        icon: <PersonAddIcon />,
        path: '/dashboard/my-structure/agencies/add',
        requiresStructure: true,
      },
    ],
  },
  {
    id: 'configuration',
    labelKey: 'dashboard.menu.configuration',
    icon: <SettingsIcon />,
    requiredProfiles: ['MANAGE_CONFIGURATIONS'],
    children: [
      {
        id: 'configuration-countries',
        labelKey: 'dashboard.menu.countries',
        icon: <CountriesIcon />,
        path: '/dashboard/configuration/countries',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-brands',
        labelKey: 'dashboard.menu.brands',
        icon: <BrandsIcon />,
        path: '/dashboard/configuration/brands',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-vehicle-types',
        labelKey: 'dashboard.menu.vehicleTypes',
        icon: <VehicleTypesIcon />,
        path: '/dashboard/configuration/vehicle-types',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-insurers',
        labelKey: 'dashboard.menu.insurers',
        icon: <InsurersIcon />,
        path: '/dashboard/configuration/insurers',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-categories',
        labelKey: 'dashboard.menu.categories',
        icon: <CategoriesIcon />,
        path: '/dashboard/configuration/categories',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-hs-codes',
        labelKey: 'dashboard.menu.hsCodes',
        icon: <HSCodesIcon />,
        path: '/dashboard/configuration/hs-codes',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-shippers',
        labelKey: 'dashboard.menu.shippers',
        icon: <ShippersIcon />,
        path: '/dashboard/configuration/shippers',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-packaging-types',
        labelKey: 'dashboard.menu.packagingTypes',
        icon: <PackagingTypesIcon />,
        path: '/dashboard/configuration/packaging-types',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-container-types',
        labelKey: 'dashboard.menu.containerTypes',
        icon: <ContainerTypesIcon />,
        path: '/dashboard/configuration/container-types',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-currencies',
        labelKey: 'dashboard.menu.currencies',
        icon: <CurrenciesIcon />,
        path: '/dashboard/configuration/currencies',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-parameters',
        labelKey: 'dashboard.menu.parameters',
        icon: <ParametersIcon />,
        path: '/dashboard/configuration/parameters',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-payment-methods',
        labelKey: 'dashboard.menu.paymentMethods',
        icon: <PaymentMethodsIcon />,
        path: '/dashboard/configuration/payment-methods',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-corridors',
        labelKey: 'dashboard.menu.corridors',
        icon: <CorridorsIcon />,
        path: '/dashboard/configuration/corridors',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-itineraries',
        labelKey: 'dashboard.menu.itineraries',
        icon: <ItinerariesIcon />,
        path: '/dashboard/configuration/itineraries',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-segments',
        labelKey: 'dashboard.menu.segments',
        icon: <SegmentsIcon />,
        path: '/dashboard/configuration/segments',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
      {
        id: 'configuration-checkpoints',
        labelKey: 'dashboard.menu.checkpointsList',
        icon: <CheckpointsIcon />,
        path: '/dashboard/configuration/checkpoints',
        requiredProfiles: ['MANAGE_CONFIGURATIONS'],
      },
    ],
  },
  // Tracking of the data exchange with the BGFT's Landfreightis platform.
  // Reserved to OGF administrators: a carrier admin also holds ADMIN.
  {
    id: 'bgft-exchanges',
    labelKey: 'dashboard.menu.bgftExchanges',
    icon: <BgftExchangesIcon />,
    path: '/dashboard/bgft-exchanges',
    requiredProfiles: ['ADMIN'],
    requiresStructureType: 'OGF',
  },
];

export function Sidebar({ open, onClose, width, isMobile }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const { colors } = useTheme();
  const { user, logout } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [qrVerifyOpen, setQrVerifyOpen] = useState(false);

  // Log an authenticated QR verify scan triggered from the sidebar entry.
  // Fire-and-forget: the scan flow is not blocked if the log call fails.
  const handleSidebarScanComplete = (
    decodedText: string,
    result: VerificationResult,
  ) => {
    void logQrScan({
      event_type: 'QR_VERIFY_SCAN_DASHBOARD',
      document_reference: result.reference ?? null,
      document_type: result.documentType ?? null,
      description: 'QR code scanned from the sidebar "Verify a document" entry',
      event_metadata: {
        source: 'sidebar',
        signature_valid: result.valid,
        error: result.error ?? null,
        raw_length: decodedText.length,
      },
    });
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if user can see a menu item based on profiles and structure requirements
  const canSeeMenuItem = (item: MenuItem): boolean => {
    // User-type rules first: they override every structure/profile allowance.
    if (item.hiddenForUserTypes && user?.user_type &&
        item.hiddenForUserTypes.includes(user.user_type)) {
      return false;
    }

    if (item.requiresUserType &&
        !(user?.user_type && item.requiresUserType.includes(user.user_type))) {
      return false;
    }

    // Check if menu requires a specific structure type (one, or any of a list)
    if (item.requiresStructureType) {
      const allowedTypes = Array.isArray(item.requiresStructureType)
        ? item.requiresStructureType
        : [item.requiresStructureType];
      if (!user?.structure_type || !allowedTypes.includes(user.structure_type)) {
        return false;
      }
    }

    // Check if menu is explicitly hidden for the user's structure type
    if (
      item.hiddenForStructureTypes &&
      user?.structure_type &&
      item.hiddenForStructureTypes.includes(user.structure_type)
    ) {
      return false;
    }

    // Check if menu requires a structure
    if (item.requiresStructure && !user?.structure_id) {
      return false;
    }

    // Hide structure/agency management for Particulier (individual structures)
    if (item.hideIfIndividualStructure && user?.structure_individual) {
      return false;
    }

    // Structure types that own this screen skip the profile check entirely
    if (
      item.allowedForStructureTypes &&
      user?.structure_type &&
      item.allowedForStructureTypes.includes(user.structure_type)
    ) {
      return true;
    }

    // Check if menu requires structure admin
    if (item.requiresStructureAdmin) {
      // User must be either structure admin or have the required profiles
      const isAdmin = user?.is_structure_admin;
      const hasProfile = item.requiredProfiles?.some((profile) => user?.profiles?.includes(profile));
      if (!isAdmin && !hasProfile) {
        return false;
      }
      return true;
    }

    // Check required profiles
    if (!item.requiredProfiles || item.requiredProfiles.length === 0) {
      return true; // No profile required, show the menu
    }
    if (!user?.profiles) {
      return false; // User has no profiles, hide the menu
    }
    // Check if user has at least one of the required profiles
    return item.requiredProfiles.some((profile) => user.profiles.includes(profile));
  };

  // Filter menu items based on user profiles and structure requirements
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .filter(canSeeMenuItem)
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          // If no children remain after filtering, hide the parent menu
          if (filteredChildren.length === 0 && !item.path) {
            return null;
          }
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item): item is MenuItem => item !== null);
  };

  const filteredMenuItems = filterMenuItems(menuItems);

  const handleMenuClick = (item: MenuItem) => {
    if (item.action === 'openVerifyDocument') {
      setQrVerifyOpen(true);
      if (isMobile) {
        onClose();
      }
      return;
    }
    if (item.children) {
      setExpandedMenus((prev) =>
        prev.includes(item.id)
          ? prev.filter((id) => id !== item.id)
          : [...prev, item.id]
      );
    } else if (item.path) {
      navigate(item.path);
      if (isMobile) {
        onClose();
      }
    }
  };

  const isMenuActive = (item: MenuItem): boolean => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.children) {
      return item.children.some((child) => location.pathname === child.path);
    }
    return false;
  };

  const isChildActive = (path?: string): boolean => {
    return path ? location.pathname === path : false;
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const isActive = isMenuActive(item);
    const isExpanded = expandedMenus.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <Box key={item.id}>
        <ListItemButton
          onClick={() => handleMenuClick(item)}
          sx={{
            pl: 2 + level * 2,
            py: 1.2,
            mx: 1,
            my: 0.3,
            borderRadius: 2,
            color: isActive
              ? colors.secondary.main
              : muiTheme.palette.text.primary,
            backgroundColor: isActive
              ? alpha(colors.secondary.main, 0.1)
              : 'transparent',
            '&:hover': {
              backgroundColor: isActive
                ? alpha(colors.secondary.main, 0.15)
                : alpha(muiTheme.palette.text.primary, 0.05),
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: isActive
                ? colors.secondary.main
                : muiTheme.palette.text.secondary,
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={t(item.labelKey)}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: isActive ? 600 : 400,
            }}
          />
          {hasChildren && (
            isExpanded ? (
              <ExpandLess sx={{ color: muiTheme.palette.text.secondary }} />
            ) : (
              <ExpandMore sx={{ color: muiTheme.palette.text.secondary }} />
            )
          )}
        </ListItemButton>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child) => {
                const childActive = isChildActive(child.path);
                return (
                  <ListItemButton
                    key={child.id}
                    onClick={() => handleMenuClick(child)}
                    sx={{
                      pl: 4 + level * 2,
                      py: 1,
                      mx: 1,
                      my: 0.2,
                      borderRadius: 2,
                      color: childActive
                        ? colors.secondary.main
                        : muiTheme.palette.text.secondary,
                      backgroundColor: childActive
                        ? alpha(colors.secondary.main, 0.1)
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: childActive
                          ? alpha(colors.secondary.main, 0.15)
                          : alpha(muiTheme.palette.text.primary, 0.05),
                      },
                      '&::before': {
                        content: '""',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: childActive
                          ? colors.secondary.main
                          : muiTheme.palette.text.disabled,
                        marginRight: 1.5,
                      },
                    }}
                  >
                    <ListItemText
                      primary={t(child.labelKey)}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: childActive ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  const sidebarContent = (
    <Box
      sx={{
        width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: muiTheme.palette.background.paper,
      }}
    >
      {/* Logo section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src="/images/logos/logo.png"
          alt="BNFT Logo"
          sx={{
            width: 50,
            height: 50,
            objectFit: 'contain',
            borderRadius: '50%',
            border: `2px solid ${colors.primary.main}`,
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: colors.primary.main,
              fontSize: '1rem',
            }}
          >
            BNFT
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: muiTheme.palette.text.secondary,
              fontSize: '0.7rem',
            }}
          >
            {t('common.appFullName')}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 2 }} />

      {/* Menu items */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          py: 1,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(muiTheme.palette.text.primary, 0.2),
            borderRadius: 3,
          },
        }}
      >
        <List component="nav" disablePadding>
          {filteredMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Logout button */}
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ p: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            py: 1.2,
            mx: 1,
            borderRadius: 2,
            color: muiTheme.palette.error.main,
            '&:hover': {
              backgroundColor: alpha(muiTheme.palette.error.main, 0.1),
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: muiTheme.palette.error.main,
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('nav.logout')}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  // Mobile: use temporary drawer
  if (isMobile) {
    return (
      <>
        <Drawer
          anchor="left"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              width,
              border: 'none',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
        <VerifyDocumentDialog
          open={qrVerifyOpen}
          onClose={() => setQrVerifyOpen(false)}
          onScanComplete={handleSidebarScanComplete}
        />
      </>
    );
  }

  // Desktop: use fixed sidebar that slides in/out
  return (
    <>
      <Box
        sx={{
          width: open ? width : 0,
          flexShrink: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: muiTheme.zIndex.drawer,
          transform: open ? 'translateX(0)' : `translateX(-${width}px)`,
          transition: muiTheme.transitions.create('transform', {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.enteringScreen,
          }),
          borderRight: `1px solid ${muiTheme.palette.divider}`,
          backgroundColor: muiTheme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {sidebarContent}
      </Box>
      <VerifyDocumentDialog
        open={qrVerifyOpen}
        onClose={() => setQrVerifyOpen(false)}
        onScanComplete={handleSidebarScanComplete}
      />
    </>
  );
}

export default Sidebar;
